import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { FaEdit, FaExclamationTriangle, FaListUl, FaWrench } from "react-icons/fa";

import myConfig from "../configs/config";
import SidebarToolInfoModal from "./SidebarToolInfoModal";
import "./SidebarStaleMeterAlertsModal.css";

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function groupAlertsByMachine(items) {
  const groups = new Map();
  (items || []).forEach((item) => {
    const key = String(item.equipmentId || item.machineIdent || item.machineName || "unknown");
    if (!groups.has(key)) {
      groups.set(key, {
        equipmentId: item.equipmentId || null,
        machineIdent: item.machineIdent || "",
        machineName: item.machineName || item.machineIdent || "—",
        machineMeterProblem: Boolean(item.machineMeterProblem),
        machineStaleCount: Number(item.machineStaleCount) || 0,
        sessions: [],
      });
    }
    const group = groups.get(key);
    group.sessions.push(item);
    if (item.machineMeterProblem) {
      group.machineMeterProblem = true;
    }
    group.machineStaleCount = Math.max(
      group.machineStaleCount,
      Number(item.machineStaleCount) || 0,
      group.sessions.length
    );
  });
  return Array.from(groups.values()).sort((left, right) =>
    String(left.machineName).localeCompare(String(right.machineName), "pt-BR")
  );
}

function buildAlertSummary(item, t) {
  const staleLabels = item.staleMeterLabels || [];
  const labelText =
    staleLabels.length > 0 ? staleLabels.join(" e ") : t("horímetro/hodômetro");
  if (item.pathDistanceM != null && item.pathDistanceM >= 100) {
    return t(
      "Leitura igual de {{meters}} com cerca de {{distance}} m percorridos no campo.",
      {
        meters: labelText,
        distance: Math.round(item.pathDistanceM),
      }
    );
  }
  return t("Mesma leitura de {{meters}} ao assumir e ao finalizar.", {
    meters: labelText,
  });
}

export default function SidebarStaleMeterAlertsModal({
  farmId,
  items,
  t,
  onClose,
}) {
  const [equipmentByIdent, setEquipmentByIdent] = useState({});

  useEffect(() => {
    if (!farmId) return;
    let cancelled = false;
    axios
      .get(`${myConfig.API_URL}/equipment/`, {
        params: { farm_id: farmId },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("client-token")}`,
        },
      })
      .then((response) => {
        if (cancelled) return;
        const rows = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data && response.data.results)
            ? response.data.results
            : [];
        const next = {};
        rows.forEach((row) => {
          if (row && row.ident != null && row.id != null) {
            next[String(row.ident)] = row.id;
          }
        });
        setEquipmentByIdent(next);
      })
      .catch(() => {
        if (!cancelled) setEquipmentByIdent({});
      });
    return () => {
      cancelled = true;
    };
  }, [farmId]);

  const groups = useMemo(() => {
    const baseGroups = groupAlertsByMachine(items);
    return baseGroups.map((group) => ({
      ...group,
      equipmentId:
        group.equipmentId ||
        equipmentByIdent[String(group.machineIdent)] ||
        null,
    }));
  }, [equipmentByIdent, items]);
  const pendingActionsHref = `/${farmId}/field-app/?tab=pending-actions&alert=stale_meter_reading`;

  return (
    <SidebarToolInfoModal
      title={t("Alertas de hodômetro/horímetro")}
      onClose={onClose}
    >
      <div className="sidebar-stale-meter-alerts">
        <p className="sidebar-stale-meter-alerts__intro">
          {t(
            "Estas máquinas têm trabalhos em que o operador informou a mesma leitura de horímetro ou hodômetro ao assumir e ao finalizar. Verifique o painel ou o sensor — horas, km e manutenções podem estar incorretos."
          )}
        </p>

        {groups.length === 0 ? (
          <p className="sidebar-stale-meter-alerts__empty">
            {t("Nenhum alerta pendente nesta fazenda.")}
          </p>
        ) : (
          <ul className="sidebar-stale-meter-alerts__list">
            {groups.map((group) => {
              const machineEditHref = group.equipmentId
                ? `/${farmId}/equipment/machine/${group.equipmentId}`
                : null;
              const latestSession = [...group.sessions].sort((left, right) =>
                String(right.finishedAt || "").localeCompare(String(left.finishedAt || ""))
              )[0];

              return (
                <li
                  key={`${group.equipmentId || group.machineIdent}`}
                  className="sidebar-stale-meter-alerts__item"
                >
                  <div className="sidebar-stale-meter-alerts__item-head">
                    <FaWrench aria-hidden="true" />
                    <div>
                      <strong>{group.machineName}</strong>
                      <span className="sidebar-stale-meter-alerts__meta">
                        {t("{{count}} ocorrência(s) pendente(s)", {
                          count: group.sessions.length,
                        })}
                        {group.machineStaleCount > group.sessions.length
                          ? ` · ${t("{{count}} na máquina", {
                              count: group.machineStaleCount,
                            })}`
                          : ""}
                      </span>
                    </div>
                  </div>

                  {group.machineMeterProblem ? (
                    <p className="sidebar-stale-meter-alerts__problem">
                      <FaExclamationTriangle aria-hidden="true" />
                      {t(
                        "Máquina marcada com possível problema no medidor. Edite o cadastro para revisar."
                      )}
                    </p>
                  ) : null}

                  {latestSession ? (
                    <p className="sidebar-stale-meter-alerts__summary">
                      {buildAlertSummary(latestSession, t)}
                      <span className="sidebar-stale-meter-alerts__date">
                        {t("Último")}: {formatDateTime(latestSession.finishedAt)}
                        {latestSession.operatorName
                          ? ` · ${latestSession.operatorName}`
                          : ""}
                      </span>
                    </p>
                  ) : null}

                  <div className="sidebar-stale-meter-alerts__actions">
                    {machineEditHref ? (
                      <Link
                        to={machineEditHref}
                        className="btn btn-sm btn-primary"
                        onClick={onClose}
                      >
                        <FaEdit aria-hidden="true" style={{ marginRight: 6 }} />
                        {t("Editar máquina")}
                      </Link>
                    ) : (
                      <span className="sidebar-stale-meter-alerts__missing">
                        {t(
                          "Cadastro da máquina não encontrado — veja os detalhes em Ações pendentes."
                        )}
                      </span>
                    )}
                    <Link
                      to={pendingActionsHref}
                      className="btn btn-sm btn-default"
                      onClick={onClose}
                    >
                      <FaListUl aria-hidden="true" style={{ marginRight: 6 }} />
                      {t("Ver em Ações pendentes")}
                    </Link>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </SidebarToolInfoModal>
  );
}
